import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, map } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Assignment, CreateAssignmentBody } from '../../../../api/app-types/assignment';
import { CaseGroup } from '../../../../api/app-types/case-group';
import { Client } from '../../../../api/app-types/client.app';
import { DisabilityKind } from '../../../../api/app-types/disability-type.app';
import { LegalFoundation } from '../../../../api/app-types/legal-foundation.app';
import { PersonType } from '../../../../api/app-types/person-type.app';
import { Sponsor } from '../../../../api/app-types/sponsor.app';
import { NO_TYPE_OF_CARE, TypeOfCare } from '../../../../api/app-types/type-of-care';
import { ASSIGNMENT_API } from '../../../../api/services/assignment.service';
import { LEGAL_FOUNDATION_SERVICE, provideLegalFoundationApi } from '../../../../api/services/legal-foundation.service';
import { provideSponsorApi, SPONSOR_SERVICE } from '../../../../api/services/sponsor.service';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrSection } from '../../../../core/components/section/section';
import { LvrSectionForm } from '../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { compareById } from '../../../../shared/helpers/compare-by-id.func';
import { isValidDate, matDateProviders, oldestPossibleDateForPhaseChange } from '../../../../shared/helpers/date.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { DB_PHASES } from '../../../../api/app-types/phase.app';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { STATE } from '../../../../core/services/state.service';
import { TENANT_FEATURES } from '../../../../core/services/tenant-feature.service';
import { maxDateValidator, minDateValidator } from '../../../../shared/validators/max-date-validator';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { hasError, hasFileError } from '../../../../shared/helpers/has-error.func';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { LvrDataProtection } from '../../../../core/components/data-protection/data-protection';
import { ClarificationValue } from '../../../../api/app-types/relative.app';
import { VALIDATION_SERVICE } from '../../../../api/services/validation.service';
import { DateRange } from '../../../../api/app-types/validation.app';

@Component({
	selector: 'lvr-create-assignment',
	imports: [
		TranslocoModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		MatButtonModule,
		LvrAutoSaveDirective,
		MatDatepickerModule,
		MatInputModule,
		MatNativeDateModule,
		LvrInputDirective,
		LvrPaddingDirective,
		DatePipe,
		LvrField,
		LvrDefaultActions,
		LvrRestoreActions,
		LvrSection,
		LvrSectionForm,
		LvrHeading,
		LvrAppendAsteriskPipe,
		DocumentUploadControl,
		LvrDataProtection,
		LvrSearchableSelect,
		RouterLink,
	],
	templateUrl: './create-assignment.html',
	styleUrl: './create-assignment.scss',
	providers: [matDateProviders, provideSponsorApi, provideLegalFoundationApi],
})
export class CreateAssignment {
	private readonly env = inject(ENVIRONMENT);
	private readonly state = inject(STATE);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly route = inject(ActivatedRoute);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	public readonly legalBaseRequiredInP2Enabled = this.tenantFeatures.legalBaseRequiredInP2Enabled();
	public readonly careRelatedDisabilityTypeRequiredInP2Enabled =
		this.tenantFeatures.careRelatedDisabilityTypeRequiredInP2Enabled();
	private readonly data = toSignal(this.route.data);
	public readonly client = computed<Client>(() => (this.data()!['client'] as ClientResolved).value as Client);
	public readonly assignment = computed(() => this.data()!['assignment'] as Assignment);
	public readonly assignmentId = computed(() => this.assignment()?.id ?? -1);

	private readonly disabilityKindsPage = computed(
		() => this.data()!['disabilityKindsPage'] as PaginatedResponse<DisabilityKind>,
	);
	public readonly disabilityKinds = computed(() => this.disabilityKindsPage().entities as DisabilityKind[]);

	public readonly typesOfCare = computed(() => this.data()!['typesOfCare'].entities as TypeOfCare[]);
	public readonly caseGroups = computed(() => this.data()!['caseGroups'].entities as CaseGroup[]);
	public readonly initiators = computed(() => this.data()!['institutions'].entities as PersonType[]);
	public readonly initiatorLabel = (option: PersonType) => option.name;
	public readonly caseGroupLabel = (option: CaseGroup) => option.description;
	public readonly sponsorLabel = (option: Sponsor) => option.name;
	public readonly legalFoundationLabel = (option: LegalFoundation) => option.description;

	private readonly draftService = inject(FormPersistenceService);
	private readonly assignmentApi = inject(ASSIGNMENT_API);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly router = inject(Router);
	private readonly sponsorService = inject(SPONSOR_SERVICE);
	private readonly legalFoundationService = inject(LEGAL_FOUNDATION_SERVICE);
	private readonly session = inject(SessionManagerService);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly showCaseGroup = computed(() => this.tenantFeatures.caseGroupEnabled());

	public readonly subjectType = DocumentFileSubjectType.ASSIGNMENT;
	public readonly canCreateAssignments = hasPermission(this.session.permissions, [
		'Assignments.Create',
		'Assignments.Create.PhaseOneOnly',
	]);
	public readonly canManageAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Create');
	public readonly maxFileCount = this.env.contactSettings.maxFiles;
	public readonly maxFileSize = this.env.fileSettings.maxFileSize;
	public readonly allowedFileTypes = this.env.fileSettings.allowedMimeTypes;

	public readonly showP2Fields = computed(() => {
		const to = this.assignment().draftPhaseId;
		return to === DB_PHASES.P2 || to === DB_PHASES.P3;
	});
	public readonly showRequiredLegalBaseInP2 = computed(
		() => this.legalBaseRequiredInP2Enabled && this.assignment().draftPhaseId === DB_PHASES.P2,
	);
	public readonly showConsultationStart = computed(() => this.assignment().draftPhaseId === DB_PHASES.P2);
	public readonly showP3Fields = computed(() => this.assignment().draftPhaseId === DB_PHASES.P3);
	public readonly showCareRelatedDisabilityTypeInP2 = computed(
		() => this.careRelatedDisabilityTypeRequiredInP2Enabled && this.assignment().draftPhaseId === DB_PHASES.P2,
	);

	public readonly draftId = computed(() => {
		return `${FORM_KEYS.CREATE_ASSIGNMENT}.${this.client().id}`;
	});

	public readonly matcher = new ImmediateErrorStateMatcher();

	public readonly availableSponsor = signal<Sponsor[]>([]);
	public readonly availableLegalFoundation = signal<LegalFoundation[]>([]);
	public readonly saving = signal(false);

	public readonly maxDate = signal(new Date());

	public readonly minDateP2 = computed(() => {
		return oldestPossibleDateForPhaseChange(
			DB_PHASES.P2,
			this.assignment(),
			this.state.previousAssignmentBeforeCurrent(),
		);
	});

	public readonly minDateP3 = computed(() => {
		return oldestPossibleDateForPhaseChange(
			DB_PHASES.P3,
			this.assignment(),
			this.state.previousAssignmentBeforeCurrent(),
		);
	});

	public readonly minDateIncapacityForWorkSince = computed(() => {
		const max = this.setDateToMidnight(this.maxDate());
		const min = new Date(max);
		min.setFullYear(max.getFullYear() - this.incapacityForWorkLimitYears);
		return min;
	});

	public readonly clarificationDateRange = signal<DateRange | undefined>(undefined);
	public readonly clarificationMinDate = computed(() => {
		return this.clarificationDateRange()?.min;
	});

	public readonly clarificationMaxDate = computed(() => {
		return this.clarificationDateRange()?.max;
	});

	public readonly form = this.initCreateAssignmentForm(inject(FormBuilder));

	private readonly typeOfCareId = toSignal(
		this.form.get('typeOfCareId')!.valueChanges.pipe(
			map(() => this.form.get('typeOfCareId')!.value),
			distinctUntilChanged(),
			map(() => {
				return this.typesOfCare().find((t) => t.id === this.form.get('typeOfCareId')!.value)?.id;
			}),
		),
	);

	public readonly compareById = compareById;
	public readonly environment = inject(ENVIRONMENT);
	public readonly dateFormatHint = this.environment.visualDateFormat;
	public readonly incapacityForWorkLimitYears = this.environment.incapacityForWorkLimitYears;
	public readonly dateWarningRange = {
		min: this.environment.formConfiguration.contact.dateWarningRange.min,
		max: this.environment.formConfiguration.contact.dateWarningRange.max, // days retroactively
	};

	private readonly sponsorId = toSignal(
		this.form.get('sponsorId')!.valueChanges.pipe(
			distinctUntilChanged(),
			map(() => this.form.get('sponsorId')!.value),
		),
		{
			initialValue: undefined,
		},
	);

	public readonly queryParams = {
		redirectUrl: this.router.url,
	};

	public constructor() {
		effect(() => {
			const typeOfCareId = this.typeOfCareId();
			const sponsorId = this.sponsorId();
			this.updateFormControls(typeOfCareId, sponsorId);
			this.getFilteredLegalFoundations(typeOfCareId, sponsorId);
		});

		effect(() => {
			const typeOfCareId = this.typeOfCareId();
			if (!typeOfCareId) return;
			this.getSponsors(typeOfCareId);
		});

		effect(() => {
			const kinds = this.disabilityKinds();
			if (kinds.length > 0) return;
			this.form.get('careRelatedHandicapId')?.disable();
		});

		effect(() => {
			const a = this.assignment();
			if (!a) return;

			const clar: ClarificationValue = {
				hasBeenClarified: a.hasBeenClarified ?? false,
				clarifiedAt: a.clarifiedAt ?? undefined,
				typeOfClarification: a.typeOfClarification ?? undefined,
			};

			this.form.get('clarification')?.setValue(clar, { emitEvent: false });
		});

		// When phase change, validators update
		effect(() => {
			this.applyValidatorsForTransition();
		});

		// Valid Clarification Date
		effect(() => {
			const assignmentId = this.assignmentId();
			if (!assignmentId) return;
			void this.loadValidClarificationDateRange(assignmentId);
		});
	}

	/**
	 * Checks if a form control has a specific validation error and has been touched.
	 * Used for LVR-Error.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns True if the any error exists and the control was touched.
	 */
	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	public hasFileError = (control: AbstractControl | null, ...errors: string[]) => hasFileError(control, ...errors);

	public saveDraftBeforeNavigation() {
		this.draftService.saveDraft('assignment', this.draftId(), this.form.getRawValue());
	}

	public getDisabilityKindName(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.disabilityType?.remark ?? '';
	}

	public getDisabilityKindDifferentiation(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.categoryDiagnosis?.description ?? '-';
	}

	/**
	 * Cancel new Assignment creation.
	 */
	public async cancel() {
		this.draftService.deleteDraft('assignment', this.draftId());
		await this.assignmentApi.resetAssignment(this.client().id, this.assignmentId(), DB_PHASES.P0);
		await this.router.navigate(['/desk/client', this.client().id, 'assignments']);
	}

	/**
	 * Creates a new Assignment.
	 */
	public async createAssignment() {
		if (this.saving()) {
			return;
		}

		const rawValue = this.form.getRawValue();
		const { clarification, documents, ...restFormValue } = rawValue;

		// Ensure Assignment is in Phase 1
		const isP0 = this.assignment()?.phase?.remark === 'P0';

		const clar = clarification ?? {
			hasBeenClarified: false,
			clarifiedAt: undefined,
			typeOfClarification: undefined,
		};

		const docs = (documents ?? []) as DocumentFile[];
		const docIds = docs.map((d: DocumentFile) => d.id);

		const payload: CreateAssignmentBody = {
			...restFormValue,
			documentIds: docIds,
			socialDataProtectionInfo: clar.hasBeenClarified,
			hasBeenClarified: clar.hasBeenClarified,
			clarifiedAt: clar.clarifiedAt,
			typeOfClarification: clar.typeOfClarification,
			...(isP0
				? {
						phaseId: DB_PHASES.P1,
						phase: { id: DB_PHASES.P1, remark: 'P1' },
						phaseStart: new Date().toISOString(),
						contactStart: this.assignment()?.contactStart,
					}
				: {}),
		};

		this.saving.set(true);
		try {
			await this.assignmentApi.create(this.client().id, this.assignmentId(), payload);
			await this.state.updateAssignments();
			await this.router.navigate(['/desk/client', this.client().id, 'assignments']);
			this.draftService.deleteDraft('assignment', this.draftId());
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.assignment_created')),
			);
		} catch (e) {
			console.error(e);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.assignment_create_error')),
			);
		} finally {
			this.saving.set(false);
		}
	}

	/**
	 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
	 * which is returned by the backend when no date is provided.
	 * @param input - Date string to validate.
	 * @returns true if the date is valid and not the default min value.
	 */
	public validDate(input?: Date | string) {
		return isValidDate(input);
	}

	/**
	 * Gets the available data and sets each result into its corresponding signal.
	 */
	private async getSponsors(typeOfCareId?: number) {
		const sponsors = (await this.sponsorService.getAllSponsorsForTypeOfCare(typeOfCareId ?? NO_TYPE_OF_CARE)).entities;
		this.availableSponsor.set(sponsors);
	}

	/**
	 * Gets the legal foundations for the current type of care
	 * and returns only those that belong to the current selected sponsor.
	 * @returns A filtered legal foundation list.
	 */
	private async getFilteredLegalFoundations(typeOfCareId?: number, sponsorId?: number) {
		if (!sponsorId) {
			this.availableLegalFoundation.set([]);
			return;
		}

		typeOfCareId = typeOfCareId ?? NO_TYPE_OF_CARE;

		const res = await this.legalFoundationService.getAllForTypeOfCareAndSponsor(typeOfCareId, sponsorId);
		this.availableLegalFoundation.set(res.entities);
	}

	private updateFormControls(typeOfCareId?: number, sponsorId?: number) {
		if (!typeOfCareId && !sponsorId) {
			this.form.get('sponsorId')?.disable();
			this.form.get('legalFoundationId')?.disable();
			this.form.get('sponsorId')?.setValue(undefined);
			this.form.get('legalFoundationId')?.setValue(undefined);
			return;
		} else if (typeOfCareId && !sponsorId) {
			this.form.get('sponsorId')?.enable();
		} else if (typeOfCareId && sponsorId) {
			this.form.get('sponsorId')?.enable();
			this.form.get('legalFoundationId')?.enable();
		}
	}

	/**
	 * Sets a new Date to the start of the midnight (00:00:00).
	 * @param date A Date.
	 * @returns A Date set at midnight.
	 */
	private setDateToMidnight(date: Date) {
		const dateAtMidnight = new Date(date);
		dateAtMidnight.setHours(0, 0, 0, 0);
		return dateAtMidnight;
	}

	/**
	 * Initializes the form for creating a new Assignment.
	 */
	private initCreateAssignmentForm(fb: FormBuilder): FormGroup {
		return fb.group({
			// P2
			initiatorId: fb.control(undefined),
			clarification: fb.control<ClarificationValue>({
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			}),
			consultationStartDate: fb.control(undefined, [
				minDateValidator(this.minDateP2()),
				maxDateValidator(this.maxDate()),
			]),
			documents: fb.control<DocumentFile[]>(
				[],
				[maxFiles(this.maxFileCount), maxFileSize(this.maxFileSize), allowedFileTypes(this.allowedFileTypes)],
			),

			// P3
			careStartDate: fb.control(undefined, [minDateValidator(this.minDateP3()), maxDateValidator(this.maxDate())]),
			typeOfCareId: fb.control(undefined),
			sponsorId: fb.control({ value: undefined, disabled: true }),
			legalFoundationId: fb.control({ value: undefined, disabled: true }),
			caseGroupId: fb.control(undefined),
			careRelatedHandicapId: fb.control(undefined),
			unemployabilitySince: fb.control(''),
			otherText: fb.control(''),
		});
	}

	private baseValidators(): Record<string, ValidatorFn[]> {
		return {
			initiatorId: [],
			clarification: [],
			consultationStartDate: [minDateValidator(this.minDateP2()), maxDateValidator(this.maxDate())],
			careStartDate: [minDateValidator(this.minDateP3()), maxDateValidator(this.maxDate())],
			typeOfCareId: [],
			sponsorId: [],
			legalFoundationId: [],
			caseGroupId: [],
			careRelatedHandicapId: [],
			documents: [maxFiles(this.maxFileCount), maxFileSize(this.maxFileSize), allowedFileTypes(this.allowedFileTypes)],
		};
	}

	private setValidators(name: string, validators: ValidatorFn[]) {
		const ctrl = this.form.get(name);
		if (!ctrl) return;
		ctrl.setValidators(validators);
		ctrl.updateValueAndValidity({ emitEvent: false });
	}

	private applyValidatorsForTransition() {
		const to = this.assignment().draftPhaseId;
		const showCaseGroup = this.showCaseGroup();
		const base = this.baseValidators();

		// Reset to base
		Object.entries(base).forEach(([name, validators]) => this.setValidators(name, validators));

		if (to === DB_PHASES.P2) {
			this.setValidators('initiatorId', [Validators.required]);
			this.setValidators('consultationStartDate', [...base['consultationStartDate'], Validators.required]);

			// The combination of the type of care, sponsor, legal base and case group is required in P2
			if (this.legalBaseRequiredInP2Enabled) {
				this.setValidators('typeOfCareId', [Validators.required]);
				this.setValidators('sponsorId', [Validators.required]);
				this.setValidators('legalFoundationId', [Validators.required]);
				this.setValidators('caseGroupId', showCaseGroup ? [Validators.required] : []);
			}
			if (this.careRelatedDisabilityTypeRequiredInP2Enabled) {
				this.setValidators('careRelatedHandicapId', [Validators.required]);
			}
			return;
		}

		if (to === DB_PHASES.P3) {
			this.setValidators('careStartDate', [...base['careStartDate'], Validators.required]);
			this.setValidators('initiatorId', [Validators.required]);
			this.setValidators('typeOfCareId', [Validators.required]);
			this.setValidators('sponsorId', [Validators.required]);
			this.setValidators('legalFoundationId', [Validators.required]);
			this.setValidators('caseGroupId', showCaseGroup ? [Validators.required] : []);
			this.setValidators('careRelatedHandicapId', [Validators.required]);
			return;
		}
	}

	/**
	 * Returns 'true' once the data protection clarification value was already given.
	 */
	public readonly isClarificationFinal = computed(() => {
		const a = this.assignment();
		return !!(a?.hasBeenClarified && a?.clarifiedAt && a?.typeOfClarification != undefined);
	});

	private async loadValidClarificationDateRange(assignmentId: number) {
		try {
			const range = await this.validationApi.getValidClarificationDateRange(assignmentId);
			this.clarificationDateRange.set(range);
		} catch (error) {
			console.error('Fehler beim Laden des gültigen Zeitraums für das Merkblatt Sozialdatenschutz', error);
			this.clarificationDateRange.set(undefined);
		}
	}
}
